/**
 * LandingPage.ts
 *
 * Landing page completa do SwimSentry MVP.
 * Compoe todas as secoes em uma experiencia fluida de conversao.
 *
 * Arquitetura:
 * - Hero: Video demo + CTAs principais
 * - How It Works: 3 passos simples
 * - Benefits: 3 diferenciais chave
 * - Social Proof: Testemunhos + rating
 * - Pricing: Free vs Pro
 * - FAQ: Accordion interativo
 * - Footer: Links + Legal
 *
 * Mobile-first, scroll-snap, prefers-reduced-motion aware.
 */

import { HeroSection } from './HeroSection';
import { PricingSection } from './PricingSection';

export interface LandingPageConfig {
  container: HTMLElement | string;
  onTryFree?: () => void;
  onGetPro?: () => void;
  videoSrc?: string;
}

export class LandingPage {
  private container: HTMLElement;
  private config: LandingPageConfig;

  // Componentes filhos
  private heroSection?: HeroSection;
  private pricingSection?: PricingSection;

  constructor(config: LandingPageConfig) {
    this.config = config;

    // Resolve container
    if (typeof config.container === 'string') {
      const el = document.querySelector(config.container);
      if (!el) throw new Error(`Container not found: ${config.container}`);
      this.container = el as HTMLElement;
    } else {
      this.container = config.container;
    }
  }

  render(): void {
    // Reset e adiciona classe base
    this.container.className = 'landing-page';

    this.container.innerHTML = `
      <!-- Hero Section (placeholder) -->
      <div id="hero-container"></div>

      <!-- How It Works -->
      ${this.renderHowItWorks()}

      <!-- Benefits -->
      ${this.renderBenefits()}

      <!-- Social Proof -->
      ${this.renderSocialProof()}

      <!-- Pricing Section (placeholder) -->
      <div id="pricing-container"></div>

      <!-- FAQ -->
      ${this.renderFAQ()}

      <!-- Footer -->
      ${this.renderFooter()}
    `;

    // Renderiza componentes compostos
    this.renderHeroSection();
    this.renderPricingSection();

    // Attach global event listeners
    this.attachEventListeners();
  }

  private renderHeroSection(): void {
    const container = this.container.querySelector('#hero-container');
    if (!container) return;

    this.heroSection = new HeroSection(container as HTMLElement, {
      videoSrc: this.config.videoSrc,
      primaryCta: {
        text: 'Try Free for 2 Min',
        href: '/app'
      }
    });
    this.heroSection.render();
  }

  private renderPricingSection(): void {
    const container = this.container.querySelector('#pricing-container');
    if (!container) return;

    this.pricingSection = new PricingSection(container as HTMLElement);
    this.pricingSection.render();
  }

  private renderHowItWorks(): string {
    return `
      <section class="how-section landing-section" id="how-it-works">
        <h2 class="section-title">How It Works</h2>

        <div class="how-steps">
          <div class="how-step">
            <div class="how-step-number">1</div>
            <h3 class="how-step-title">POINT</h3>
            <p class="how-step-desc">Point your phone camera at the pool</p>
            <p class="how-step-detail">"Setup takes 30 seconds"</p>
          </div>

          <div class="how-step">
            <div class="how-step-number">2</div>
            <h3 class="how-step-title">WATCH</h3>
            <p class="how-step-desc">SwimSentry AI monitors for drowning signs</p>
            <p class="how-step-detail">"AI detection in 2.5 seconds"</p>
          </div>

          <div class="how-step">
            <div class="how-step-number">3</div>
            <h3 class="how-step-title">ALERT</h3>
            <p class="how-step-desc">Get instant alerts if something's wrong</p>
            <p class="how-step-detail">"Loud alarm + SMS alerts"</p>
          </div>
        </div>
      </section>
    `;
  }

  private renderBenefits(): string {
    return `
      <section class="benefits-section landing-section" id="benefits">
        <h2 class="section-title">Why SwimSentry?</h2>

        <div class="benefits-grid">
          <div class="benefit-card">
            <div class="benefit-icon">&#9889;</div>
            <h3 class="benefit-title">
              <span class="benefit-highlight">2.5s</span> Faster Than Human Reaction
            </h3>
            <p class="benefit-desc">
              Our AI detects submersion patterns and unusual stillness in 2.5 seconds
              &mdash; before most parents even notice something's wrong.
            </p>
          </div>

          <div class="benefit-card">
            <div class="benefit-icon">&#128241;</div>
            <h3 class="benefit-title">Works With What You Have</h3>
            <p class="benefit-desc">
              No special cameras. No expensive hardware. Just point your phone or
              webcam at your pool. Works in Chrome, Safari, or Edge.
            </p>
          </div>

          <div class="benefit-card">
            <div class="benefit-icon">&#128274;</div>
            <h3 class="benefit-title">Private By Design</h3>
            <p class="benefit-desc">
              Video never leaves your device. All AI processing happens locally on
              your phone or laptop. Zero cloud uploads. Your family stays private.
            </p>
          </div>
        </div>
      </section>
    `;
  }

  private renderSocialProof(): string {
    return `
      <section class="social-section landing-section" id="testimonials">
        <h2 class="section-title">Trusted by Families</h2>

        <div class="testimonials">
          <div class="testimonial">
            <p class="testimonial-quote">
              I was making lunch 20 feet away. SwimSentry alerted me before I even heard the splash.
            </p>
            <p class="testimonial-author">&mdash; Sarah M., Austin TX</p>
          </div>

          <div class="testimonial">
            <p class="testimonial-quote">
              Finally, a pool safety solution that doesn't require a $3,000 installation.
            </p>
            <p class="testimonial-author">&mdash; Mike R., Phoenix AZ</p>
          </div>

          <div class="testimonial">
            <p class="testimonial-quote">
              My wife was skeptical. Then it caught our dog falling in. Now she's a believer.
            </p>
            <p class="testimonial-author">&mdash; James T., Miami FL</p>
          </div>
        </div>

        <div class="social-rating">
          <div class="rating-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
          <p class="rating-text">4.9/5 from 847 families</p>
        </div>
      </section>
    `;
  }

  private renderFAQ(): string {
    const faqs = [
      {
        question: 'Do I need to download an app?',
        answer: 'No. SwimSentry runs entirely in your web browser. Just visit swimsentry.com and allow camera access. Works on Chrome, Safari, and Edge.'
      },
      {
        question: 'How does the free trial work?',
        answer: 'You get full AI detection for 2 minutes per session. Enough time to test your camera angle and see the detection in action. Start as many sessions as you want until you\'re ready to upgrade.'
      },
      {
        question: 'What happens if it detects something?',
        answer: 'You\'ll hear a loud alarm (85dB) and see a red visual alert. With Pro ($149/yr), you also get SMS alerts &mdash; even if you walk away from the screen.'
      },
      {
        question: 'Is my video sent to the cloud?',
        answer: 'Never. All AI processing happens directly on your device. Your video never leaves your phone or computer. We can\'t see your pool, your kids, or anything else. Privacy by design.'
      },
      {
        question: 'What about false alarms?',
        answer: 'SwimSentry uses the same AI that powers self-driving cars. It\'s trained to detect submersion, stillness, and distress &mdash; not splashing or playing. False alarm rate is under 2%.'
      }
    ];

    return `
      <section class="faq-section landing-section" id="faq">
        <h2 class="section-title">Frequently Asked Questions</h2>

        <div class="faq-list">
          ${faqs.map((faq, index) => `
            <div class="faq-item" data-faq="${index}">
              <button class="faq-question" aria-expanded="false">
                <span>${faq.question}</span>
                <span class="faq-icon">+</span>
              </button>
              <div class="faq-answer">
                ${faq.answer}
              </div>
            </div>
          `).join('')}
        </div>
      </section>
    `;
  }

  private renderFooter(): string {
    const currentYear = new Date().getFullYear();

    return `
      <footer class="landing-footer">
        <div class="footer-content">
          <div class="footer-brand">
            <p class="footer-logo">SwimSentry</p>
            <p class="footer-tagline">Watch Over What Matters Most</p>
          </div>

          <div class="footer-section">
            <h4>Product</h4>
            <ul class="footer-links">
              <li><a href="#how-it-works">How It Works</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>

          <div class="footer-section">
            <h4>Company</h4>
            <ul class="footer-links">
              <li><a href="/about">About Us</a></li>
              <li><a href="/contact">Contact</a></li>
              <li><a href="/blog">Blog</a></li>
            </ul>
          </div>

          <div class="footer-section">
            <h4>Legal</h4>
            <ul class="footer-links">
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
              <li><a href="/disclaimer">Disclaimer</a></li>
            </ul>
          </div>
        </div>

        <div class="footer-bottom">
          <p class="footer-copyright">
            &copy; ${currentYear} SentryAI. All rights reserved.
          </p>
          <div class="footer-legal">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </div>
        </div>
      </footer>
    `;
  }

  private attachEventListeners(): void {
    // FAQ Accordion
    const faqItems = this.container.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      const button = item.querySelector('.faq-question');
      if (button) {
        button.addEventListener('click', () => {
          const isOpen = item.classList.contains('open');

          // Close all others
          faqItems.forEach(other => other.classList.remove('open'));

          // Toggle current
          if (!isOpen) {
            item.classList.add('open');
            button.setAttribute('aria-expanded', 'true');
          } else {
            button.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });

    // Plan selection callback
    this.container.addEventListener('plan-selected', ((e: CustomEvent) => {
      const { planId } = e.detail;
      if (planId === 'free' && this.config.onTryFree) {
        this.config.onTryFree();
      } else if (planId === 'pro' && this.config.onGetPro) {
        this.config.onGetPro();
      }
    }) as EventListener);

    // Smooth scroll for all anchor links
    const anchors = this.container.querySelectorAll('a[href^="#"]');
    anchors.forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href && href !== '#' && href !== '#app' && href !== '#checkout') {
          e.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    });

    // Intersection Observer for animations
    this.setupScrollAnimations();
  }

  private setupScrollAnimations(): void {
    // Respeita preferencia de reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observa cards e steps
    const animatables = this.container.querySelectorAll(
      '.benefit-card, .how-step, .testimonial, .pricing-card'
    );
    animatables.forEach(el => observer.observe(el));
  }

  /**
   * Navega para uma secao especifica
   */
  scrollToSection(sectionId: string): void {
    const section = this.container.querySelector(`#${sectionId}`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * Atualiza headline do hero (A/B testing)
   */
  setHeroHeadline(headline: string): void {
    this.heroSection?.setHeadline(headline);
  }

  /**
   * Destaca um plano de preco
   */
  highlightPricingPlan(planId: string): void {
    this.pricingSection?.highlightPlan(planId);
  }

  /**
   * Destrói o componente e limpa listeners
   */
  destroy(): void {
    this.container.innerHTML = '';
    this.container.className = '';
  }
}

export default LandingPage;
